import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { Thread, ThreadMessageWithStatus, EmailActionContext, Participant } from '@/types/email';
import type { ReplyContext } from '@/types/ui';
import { useAccountStore } from '@/stores/accountStore';
import { useEmailStore } from '@/stores/emailStore';
import { executeEmailAction } from '@/stores/emailCommands';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { EmailDetail } from './EmailDetail';
import { ActionButtons } from './components/ActionButtons';
import { MobileActionBar } from './components/MobileActionBar';
import type { ReplyMode } from './components/MessageMetadata';
import { buildReplyContextWithFallback } from './utils/replyContext';

interface ThreadDetailViewProps {
  thread: Thread;
  loading?: boolean;
  onBack?: () => void;
  onDelete: (emailId: string, context?: EmailActionContext) => void;
  onArchive: (emailId: string, context?: EmailActionContext) => void;
  onArchiveThread?: () => void;
  onEmailUpdate?: () => void;
  onReply?: (context: ReplyContext) => void;
  onMarkRead?: (emailId: string) => void;
  accountId: string | null;
  highlightSenderId?: string | null;
  highlightEmailId?: string | null;
  onApproveSender?: (contactId: string) => void;
  onBlockSender?: (contactId: string) => void;
  operationPending?: boolean;
  isSent?: boolean;
}

function formatParticipants(participants: Participant[], maxDisplay = 3): string {
  if (participants.length === 0) return '';
  const displayNames = participants
    .slice(0, maxDisplay)
    .map(p => p.name || p.email.split('@')[0]);
  if (participants.length > maxDisplay) {
    return `${displayNames.join(', ')} +${participants.length - maxDisplay} more`;
  }
  return displayNames.join(', ');
}

interface CollapsibleMessageProps {
  message: ThreadMessageWithStatus;
  isExpanded: boolean;
  onToggle: () => void;
  highlighted?: boolean;
  onDelete: (emailId: string, context?: EmailActionContext) => void;
  onArchive: (emailId: string, context?: EmailActionContext) => void;
  onEmailUpdate?: () => void;
  onReply?: (context: ReplyContext) => void;
  accountId: string | null;
  onApproveSender?: (contactId: string) => void;
  onBlockSender?: (contactId: string) => void;
  operationPending?: boolean;
  isSent?: boolean;
}

function CollapsibleMessage({
  message,
  isExpanded,
  onToggle,
  highlighted,
  onDelete,
  onArchive,
  onEmailUpdate,
  onReply,
  accountId,
  onApproveSender,
  onBlockSender,
  operationPending,
  isSent,
}: CollapsibleMessageProps) {
  const statusLabel = message.isTrashed
    ? 'Deleted'
    : message.isArchived
      ? 'Archived'
      : null;

  if (!isExpanded) {
    // Collapsed view for archived/deleted messages
    return (
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'w-full text-left px-4 py-3 border rounded-lg transition-colors',
          'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring',
          message.isTrashed && 'border-destructive/30 bg-destructive/5',
          message.isArchived && !message.isTrashed && 'border-muted bg-muted/30',
          highlighted && 'border-l-4 border-l-primary'
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">
                {message.from.name || message.from.email}
              </span>
              {statusLabel && (
                <span className={cn(
                  'text-xs px-1.5 py-0.5 rounded',
                  message.isTrashed ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
                )}>
                  {statusLabel}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground truncate mt-0.5">
              {message.snippet || message.subject}
            </p>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="text-xs whitespace-nowrap">
              {new Date(message.date).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
      </button>
    );
  }

  // Expanded view
  return (
    <div
      className={cn(
        'border rounded-lg overflow-hidden',
        highlighted && 'border-l-4 border-l-primary',
        message.isTrashed && 'border-destructive/30',
        message.isArchived && !message.isTrashed && 'border-muted'
      )}
    >
      {(message.isArchived || message.isTrashed) && (
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-2 bg-muted/50 hover:bg-muted/70 transition-colors"
        >
          <span className="text-xs font-medium text-muted-foreground">
            {statusLabel}
          </span>
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        </button>
      )}
      <EmailDetail
        email={message}
        onDelete={onDelete}
        onArchive={onArchive}
        onEmailUpdate={onEmailUpdate}
        onReply={onReply}
        accountId={accountId}
        mode="embedded"
        context="thread"
        highlighted={highlighted}
        onApproveSender={onApproveSender}
        onBlockSender={onBlockSender}
        operationPending={operationPending}
        isSent={isSent}
      />
    </div>
  );
}

export function ThreadDetailView({
  thread,
  loading,
  onBack,
  onDelete,
  onArchive,
  onArchiveThread,
  onEmailUpdate,
  onReply,
  onMarkRead,
  accountId,
  highlightSenderId,
  highlightEmailId,
  onApproveSender,
  onBlockSender,
  operationPending,
  isSent,
}: ThreadDetailViewProps) {
  const accounts = useAccountStore((state) => state.accounts);
  // Review finding 3: with a provisional shell the thread-load failure no longer
  // routes through the standalone EmailDetail's D9 banner, so surface the retry
  // here. refreshSelectedThread re-fetches in place and clears the failed flag.
  const threadLoadFailed = useEmailStore((state) => state.selectedThreadLoadFailed);
  const handleRetryThread = useCallback(() => {
    void useEmailStore.getState().refreshSelectedThread(thread.id, accountId ?? thread.accountId);
  }, [thread.id, thread.accountId, accountId]);

  // Set of user's own email addresses (lowercase) for filtering CC on Reply All
  const accountEmails = useMemo(() => {
    const emails = new Set<string>();
    accounts.forEach((account) => {
      if (account.emailAddress) {
        emails.add(account.emailAddress.toLowerCase());
      }
    });
    return emails;
  }, [accounts]);

  // Track which archived/deleted messages are expanded
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  // Refs for scrolling to highlighted email
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasScrolledRef = useRef(false);

  // The ScrollArea viewport element — used as the IntersectionObserver root so
  // read-on-view is computed against the real scroll container (a state-backed
  // callback ref so the observer effect re-runs once the viewport mounts).
  const [scrollViewport, setScrollViewport] = useState<HTMLDivElement | null>(null);

  // Track which messages have been marked as read (to avoid duplicate calls)
  const markedAsReadRef = useRef<Set<string>>(new Set());

  // Messages sorted chronologically (oldest first)
  const sortedMessages = useMemo(() => {
    return [...thread.messages].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [thread.messages]);

  // Determine which messages should start collapsed (archived or deleted)
  const isMessageCollapsible = useCallback((message: ThreadMessageWithStatus) => {
    return message.isArchived || message.isTrashed;
  }, []);

  const isMessageExpanded = useCallback(
    (message: ThreadMessageWithStatus) => {
      // Non-collapsible messages are always expanded
      if (!isMessageCollapsible(message)) return true;
      // Collapsible messages check the expanded set
      return expandedMessages.has(message.id);
    },
    [expandedMessages, isMessageCollapsible]
  );

  const toggleMessage = useCallback((messageId: string) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  // Check if a message should be highlighted (by email ID or sender)
  const isHighlighted = useCallback(
    (message: ThreadMessageWithStatus) => {
      // First check if this specific email is highlighted
      if (highlightEmailId && message.id === highlightEmailId) return true;
      // Then check if the sender is highlighted
      if (!highlightSenderId) return false;
      return message.from.email === highlightSenderId ||
        message.contactId === highlightSenderId ||
        (message as any).fromParticipantId === highlightSenderId;
    },
    [highlightEmailId, highlightSenderId]
  );

  // Scroll to highlighted email. Review finding 4: with a provisional shell the
  // target is the ONLY message at first (already at the top), so a one-shot
  // scroll+lock would fire on the shell and then never re-run when siblings
  // hydrate ABOVE the target and push it off-screen. Re-scroll on every list
  // change until the FULL conversation is present, then lock.
  useEffect(() => {
    if (!highlightEmailId || hasScrolledRef.current) return;

    const total = thread.messageCount ?? thread.messages.length;
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const targetRef = messageRefs.current.get(highlightEmailId);
      if (targetRef) {
        targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (thread.messages.length >= total) {
          hasScrolledRef.current = true;
        }
      }
    });
  }, [highlightEmailId, sortedMessages, thread.messageCount, thread.messages.length]);

  // Set up IntersectionObserver to mark messages as read when they scroll into view
  useEffect(() => {
    if (!onMarkRead) return;

    // Build a map of message IDs to their read status and visibility
    const unreadMessages = new Map<string, ThreadMessageWithStatus>();
    for (const message of sortedMessages) {
      // Only track unread, non-draft messages that haven't been marked yet
      if (!message.read && !message.draft && !markedAsReadRef.current.has(message.id)) {
        unreadMessages.set(message.id, message);
      }
    }

    if (unreadMessages.size === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          // Find which message this element corresponds to
          const messageId = Array.from(messageRefs.current.entries()).find(
            ([, el]) => el === entry.target
          )?.[0];

          if (!messageId) continue;

          const message = unreadMessages.get(messageId);
          if (!message) continue;

          // Only EXPANDED messages count as read: scrolling past a COLLAPSED
          // archived/trashed stub must not mark it (D13).
          if (!isMessageExpanded(message)) continue;

          // Mark as read if message is visible and unread
          if (!markedAsReadRef.current.has(messageId)) {
            markedAsReadRef.current.add(messageId);
            onMarkRead(messageId);
          }

          // Stop observing this element
          observer.unobserve(entry.target);
        }
      },
      {
        // Mark as read when at least 30% of the message is visible
        threshold: 0.3,
        // Messages scroll inside the Radix ScrollArea viewport, not the window —
        // compute intersection against that element. Falls back to the browser
        // viewport until the viewport ref is attached.
        root: scrollViewport,
      }
    );

    // Observe all message elements
    for (const [messageId] of unreadMessages) {
      const element = messageRefs.current.get(messageId);
      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [sortedMessages, onMarkRead, scrollViewport, isMessageExpanded]);

  // Active (non-archived, non-trashed) messages — the set thread-level
  // actions operate on, matching the header Archive button and keyboard.
  const activeMessages = useMemo(
    () => sortedMessages.filter(m => !m.isArchived && !m.isTrashed),
    [sortedMessages]
  );

  // Get the last non-archived message for reply context
  const lastActiveMessage = useMemo(() => {
    return activeMessages[activeMessages.length - 1] ?? sortedMessages[sortedMessages.length - 1];
  }, [activeMessages, sortedMessages]);

  const handleReplyMode = useCallback(
    async (mode: ReplyMode) => {
      if (!lastActiveMessage || !onReply) return;
      onReply(await buildReplyContextWithFallback(lastActiveMessage, mode, { accountEmails }));
    },
    [lastActiveMessage, onReply, accountEmails]
  );

  // Thread-level actions for the action bars (desktop header + mobile). Same per-message
  // 'messages' targeting as handleArchiveThread — never 'threadRows'.
  const handleDeleteThread = useCallback(() => {
    const ids = activeMessages.map((message) => message.id);
    if (!ids.length) return;
    void executeEmailAction('delete', { kind: 'messages', ids });
    onBack?.();
  }, [activeMessages, onBack]);

  const handleThreadMarkRead = useCallback(
    (_emailId: string, read: boolean) => {
      const ids = activeMessages.map((message) => message.id);
      if (!ids.length) return;
      void executeEmailAction(read ? 'markRead' : 'markUnread', { kind: 'messages', ids });
    },
    [activeMessages]
  );

  const handleThreadMove = useCallback(() => {
    const ids = activeMessages.map((message) => message.id);
    if (!ids.length) return;
    useMoveToFolderStore.getState().open(ids);
  }, [activeMessages]);

  // Only block the whole pane on the spinner when we have NOTHING to show yet.
  // With a provisional shell (target message already loaded, siblings streaming —
  // PERF_AUDIT P1), render the message immediately and surface the remaining
  // load as a subtle inline hint (below) instead of hiding everything.
  if (loading && thread.messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!thread || thread.messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No messages in thread
      </div>
    );
  }

  const participantList = formatParticipants(thread.participants);
  // Declared conversation size vs how many messages we've actually hydrated —
  // drives the "N messages" label and the streaming hint while siblings load.
  const totalMessageCount = thread.messageCount ?? thread.messages.length;
  const siblingsLoading = Boolean(loading) && thread.messages.length < totalMessageCount;
  // Review finding 5: while the shell is incomplete (siblings still hydrating or
  // the sibling fetch failed) selectedThread.messages holds only the seed, so
  // thread-LEVEL bulk actions would silently operate on a single message while
  // presenting as "all N". Gate them until the full conversation is materialized.
  const isThreadIncomplete = thread.messages.length < totalMessageCount;
  // Partial shell whose sibling fetch FAILED (not still loading) — show retry.
  const showThreadLoadError = threadLoadFailed && !loading && isThreadIncomplete;
  const activeCount = activeMessages.length;
  const archivedCount = sortedMessages.filter(m => m.isArchived && !m.isTrashed).length;
  const deletedCount = sortedMessages.filter(m => m.isTrashed).length;
  const allThreadRead = activeMessages.every((message) => message.read);
  const conversationScope = `conversation (${activeCount} message${activeCount === 1 ? '' : 's'})`;
  const incompleteHint = 'Loading the full conversation…';

  return (
    <div className="flex flex-col h-full">
      {/* Thread Header */}
      <div className="flex-shrink-0 border-b px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-3">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="flex-shrink-0 h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-semibold truncate">
              {thread.subject}
            </h1>
            <div className="flex flex-wrap items-center gap-x-1 sm:gap-x-2 gap-y-0.5 text-xs sm:text-sm text-muted-foreground">
              <span className="shrink-0">
                {totalMessageCount} messages
                {siblingsLoading && (
                  <span className="ml-1 inline-flex items-center gap-1 text-muted-foreground/70">
                    <span className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-current" />
                  </span>
                )}
              </span>
              {archivedCount > 0 && (
                <span className="text-xs shrink-0">({archivedCount} archived)</span>
              )}
              {deletedCount > 0 && (
                <span className="text-xs text-destructive shrink-0">({deletedCount} deleted)</span>
              )}
              <span className="mx-0.5 sm:mx-1 shrink-0">·</span>
              <span className="truncate min-w-0">{participantList}</span>
            </div>
          </div>
          {/* Thread-level actions: the same desktop grammar as the single
              email header (archive / delete / mark / reply cluster / More),
              scoped to all active messages in the conversation. */}
          {lastActiveMessage && (
            <ActionButtons
              className="flex-shrink-0"
              emailId={lastActiveMessage.id}
              onArchive={() => onArchiveThread?.()}
              archiveDisabled={isThreadIncomplete || !onArchiveThread}
              onDelete={handleDeleteThread}
              deleteDisabled={isThreadIncomplete}
              isRead={allThreadRead}
              onMarkRead={handleThreadMarkRead}
              markDisabled={isThreadIncomplete}
              onReply={onReply ? (mode) => void handleReplyMode(mode) : undefined}
              onMoveToFolder={handleThreadMove}
              moveDisabled={isThreadIncomplete}
              tooltips={{
                archive: isThreadIncomplete ? incompleteHint : `Archive ${conversationScope}`,
                delete: isThreadIncomplete ? incompleteHint : `Delete ${conversationScope}`,
                markRead: `Mark ${conversationScope} read`,
                markUnread: `Mark ${conversationScope} unread`,
                move: 'Move conversation to folder',
              }}
            />
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1" viewportRef={setScrollViewport}>
        <div className="p-3 sm:p-4 pb-28 md:pb-4 space-y-3 sm:space-y-4">
          {showThreadLoadError && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-warning-line/60 bg-warning-surface px-3 py-2 text-sm text-warning-ink">
              <span>Couldn&apos;t load the rest of this conversation.</span>
              <Button variant="outline" size="sm" onClick={handleRetryThread} className="flex-shrink-0">
                Retry
              </Button>
            </div>
          )}
          {sortedMessages.map((message) => (
            <div
              key={message.id}
              ref={(el) => {
                if (el) {
                  messageRefs.current.set(message.id, el);
                } else {
                  messageRefs.current.delete(message.id);
                }
              }}
            >
              <CollapsibleMessage
                message={message}
                isExpanded={isMessageExpanded(message)}
                onToggle={() => toggleMessage(message.id)}
                highlighted={isHighlighted(message)}
                onDelete={onDelete}
                onArchive={onArchive}
                onEmailUpdate={onEmailUpdate}
                onReply={onReply}
                accountId={accountId}
                onApproveSender={onApproveSender}
                onBlockSender={onBlockSender}
                operationPending={operationPending}
                isSent={isSent}
              />
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Mobile action bar: thread-level actions (same sets as the desktop
          header/reply chrome it replaces on small screens) */}
      {lastActiveMessage && (
        <MobileActionBar
          emailId={lastActiveMessage.id}
          isRead={activeMessages.every((message) => message.read)}
          onArchive={() => onArchiveThread?.()}
          archiveDisabled={isThreadIncomplete || !onArchiveThread}
          onDelete={handleDeleteThread}
          deleteDisabled={isThreadIncomplete}
          onReply={(mode) => void handleReplyMode(mode)}
          onMarkRead={handleThreadMarkRead}
          onMoveToFolder={handleThreadMove}
        />
      )}
    </div>
  );
}
